import React from 'react';

/**
 * A simplified component that renders an avatar based on the provided properties
 * @property {string} [name=Anonymous] - the name to be used to get the initials
 * @property {string} [className] - additional class names for the avatar
 * @property {string} [backgroundColor=#233a91] - the background color to use when no image is provided
 * @property {number} [height=24] - the height of the avatar
 * @property {number} [width=24] - the width of the avatar
 * @returns {ReactElement} - the avatar element
 */
const Avatar = ({
  name = '',
  className = 'text-md font-semibold rounded-full text-gray-500',
  backgroundColor = '#F7F7F7',
  height = 36,
  width = 36,
}: IAvatarProps): React.ReactElement => {
  const getInitials = (name: string): string => {
    if (!name) return '';

    // Split the name by any special character or space
    const parts = name.split(/[^a-zA-Z]/).filter(Boolean);

    if (parts.length === 0) return '';

    if (parts.length === 1) {
      // For single part, take up to first two letters
      return parts[0].substring(0, 2).toUpperCase();
    }

    // For multiple parts, take first letter of first part and first letter of next valid part
    const firstLetter = parts[0][0];
    const secondLetter = parts[1][0];

    return (firstLetter + secondLetter).toUpperCase();
  };

  const styles = {
    height: `${height}px`,
    width: `${width}px`,
    backgroundColor: backgroundColor,
  };

  return (
    <div
      style={styles}
      className={`flex justify-center items-center shrink-0  ${className}`}
      role="img"
      aria-label={name}
    >
      {getInitials(name)}
    </div>
  );
};

/**
 * Props for the Avatar component.
 */
interface IAvatarProps {
  imageUrl?: string;
  name?: string;
  className?: string;
  backgroundColor?: string;
  height?: number;
  width?: number;
}

export default Avatar;
